import { useEffect, useMemo, useState } from 'react';
import {
  getConnectorGroup,
  fetchMonthlySummary as fetchConnectorGroupMonthlySummary,
  fetchWeeklySummary as fetchConnectorGroupWeeklySummary
} from 'services/connectorGroupService';
import { useAuth } from './useAuth';
import {
  fetchConnectorsForConnectorGroup,
  createConnector as createNewConnector,
  updateConnector as updateExistingConnector,
  deleteConnector as deleteExistingConnector,
} from 'services/connectorService';
import { Connector } from 'models/Connector';
import { useEntities } from './useEntities';
import { calculateAverage, formatSummaryDates } from 'utils/summary';


const useConnectorGroup = (connector_group_id: number) => {

  const { accessToken } = useAuth();
  const { connectorsById, upsertConnectorGroups, upsertConnectors } = useEntities();
  const [connectorGroup, setConnectorGroup] = useState<any>(null);
  const [connectorIds, setConnectorIds] = useState<number[]>([]);
  const [monthlySummary, setMonthlySummary] = useState<any[]>([]);
  const [weeklySummary, setWeeklySummary] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const connectors = useMemo(
    () => connectorIds.map(id => connectorsById[id]).filter(Boolean),
    [connectorIds, connectorsById]
  );

  const executeWithLoading = async (asyncFunction: () => Promise<void>) => {
    setLoading(true);
    setError(null);
    try {
      await asyncFunction();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectors = async () => {
    await executeWithLoading(async () => {
      const data = await fetchConnectorsForConnectorGroup(connector_group_id, accessToken);
      upsertConnectors(data);
      setConnectorIds(data.map(connector => connector.id));
    });
  }

  const createConnector = async (newConnector: Connector) => {
    await executeWithLoading(async () => {
      await createNewConnector(newConnector, accessToken);
      await fetchConnectors();
    });
  };

  const updateConnector = async (id: number, updatedConnector: Connector) => {
    await executeWithLoading(async () => {
      await updateExistingConnector(id, updatedConnector, accessToken);
      await fetchConnectors();
    });
  };

  const deleteConnector = async (id: number) => {
    await executeWithLoading(async () => {
      await deleteExistingConnector(id, accessToken);
      await fetchConnectors();
    });
  };

  const fetchProfile = async () => {
    await executeWithLoading(async () => {
      const data = await getConnectorGroup(connector_group_id, accessToken);
      setConnectorGroup(data);
      upsertConnectorGroups([data]);
    });
  };

  const fetchConnectorGroupMonthlyData = async () => {
    await executeWithLoading(async () => {
      const monthlyData = await fetchConnectorGroupMonthlySummary(connector_group_id, accessToken);
        const formattedMonthlyData = formatSummaryDates(monthlyData, 'period_start_date');
    
        setMonthlySummary(formattedMonthlyData); 
           });
  };

  const fetchConnectorGroupWeeklyData = async () => {
    await executeWithLoading(async () => {
      const weeklyData = await fetchConnectorGroupWeeklySummary(connector_group_id, accessToken);
    const formattedWeeklyData = formatSummaryDates(weeklyData, 'period_start_date');
    
    setWeeklySummary(formattedWeeklyData);
      });
  };


  const calculateMonthlyAverage = async (connector_group_id: number, accessToken: string, key: string): Promise<string> => {
    const monthlySummary = await fetchConnectorGroupMonthlySummary(connector_group_id, accessToken);
    return calculateAverage(monthlySummary, key);
  };


  useEffect(() => {
    const fetchData = async () => {
      if (!connector_group_id || !accessToken) {
        setConnectorGroup(null);
        setConnectorIds([]);
        setMonthlySummary([]);
        setWeeklySummary([]);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      await fetchProfile();
      await fetchConnectors();
      await fetchConnectorGroupMonthlyData();
      await fetchConnectorGroupWeeklyData();
    };

    fetchData();
  }, [connector_group_id, accessToken]);

  return {
    connectorGroup,
    connectors,
    monthlySummary,
    weeklySummary,
    createConnector,
    updateConnector,
    deleteConnector,
    calculateMonthlyAverage,
    accessToken,
    loading,
    error
  };
};

export default useConnectorGroup;
